import { Badge, Button, Card, Center, Group, Loader, Stack, Text, ThemeIcon } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconServer, IconTestPipe } from '@tabler/icons-react';

import { SearchBox, Table, TableColumnProps } from '@components';
import { IntegrationTestGeneratorView } from './IntegrationTestGeneratorView';
import { formatDate, getStatusColor } from './request-logger.utils';

import type { RequestLog } from '@admin/hooks';

interface RequestLogsTabProps {
  requests: RequestLog[];
  total: number;
  isLoading: boolean;
  searchQuery: string;
  page: number;
  size: number;
  onSearchChange: (value: string) => void;
  onPaginationChange: (page: number, size: number) => void;
  onRowClick: (requestId: string) => void;
}

export const RequestLogsTab = ({
  requests,
  total,
  isLoading,
  searchQuery,
  page,
  size,
  onSearchChange,
  onPaginationChange,
  onRowClick,
}: RequestLogsTabProps) => {
  const openBatchRequestsModal = () => {
    modals.open({
      title: 'Generate Integration Tests',
      size: 'xl',
      centered: true,
      children: (
        <IntegrationTestGeneratorView
          onRequestClick={(requestId) => {
            modals.closeAll();
            onRowClick(requestId);
          }}
        />
      ),
    });
  };

  const columns: TableColumnProps<RequestLog>[] = [
    {
      name: 'Request ID',
      cell: (row) => (
        <Text size="xs" style={{ fontFamily: 'monospace' }}>
          {row.requestId?.substring(0, 8) || 'N/A'}
        </Text>
      ),
      width: '100px',
    },
    {
      name: 'Type',
      cell: (row) => (
        <Badge color={row.requestType === 'inbound' ? 'blue' : 'orange'} size="sm" variant="light">
          {row.requestType}
        </Badge>
      ),
      width: '100px',
    },
    {
      name: 'Method',
      cell: (row) => (
        <Badge color="gray" size="sm" variant="light">
          {row.request?.method || 'N/A'}
        </Badge>
      ),
      width: '80px',
    },
    {
      name: 'URL',
      cell: (row) => (
        <Text size="xs" lineClamp={1} title={row.request?.url}>
          {row.request?.url || 'N/A'}
        </Text>
      ),
    },
    {
      name: 'Status',
      cell: (row) => (
        <Badge color={getStatusColor(row.response?.status)} size="sm" variant="light">
          {row.response?.status || 'N/A'}
        </Badge>
      ),
      width: '80px',
    },
    {
      name: 'IP',
      cell: (row) => (
        <Text size="xs" style={{ fontFamily: 'monospace' }}>
          {row.request?.ip || 'N/A'}
        </Text>
      ),
      width: '120px',
    },
    {
      name: 'Time',
      cell: (row) => (
        <Text size="xs" title={formatDate(row.createdAt)}>
          {formatDate(row.createdAt)}
        </Text>
      ),
      width: '180px',
    },
  ];

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Stack gap="md">
        <Group justify="space-between">
          <Group gap="xs">
            <ThemeIcon color="blue" variant="light" radius="xl" size="md">
              <IconServer size={16} />
            </ThemeIcon>
            <Text size="lg" fw={600}>
              Request Logs
            </Text>
          </Group>
          <Group gap="md">
            <Button leftSection={<IconTestPipe size={16} />} variant="gradient" gradient={{ from: 'indigo', to: 'cyan' }} onClick={openBatchRequestsModal}>
              Generate Integration Tests
            </Button>
            <SearchBox placeholder="Search requests by URL prefix..." value={searchQuery} handleSearch={onSearchChange} data-pw="request-logger-search-box" />
          </Group>
        </Group>

        {isLoading ? (
          <Center style={{ height: '200px' }}>
            <Loader size="xl" />
          </Center>
        ) : (
          <Table
            columns={columns}
            dataSource={requests}
            total={total}
            onPaginationChange={onPaginationChange}
            onRowClick={(row) => onRowClick(row._id)}
            defaultPage={page}
            defaultPerPage={size}
            verticalSpacing="xs"
          />
        )}
      </Stack>
    </Card>
  );
};
